import { useState, useEffect } from 'react';

interface ViewportInfo {
  height: number;
  availableHeight: number;
  bottomInset: number;
  hasBottomNav: boolean;
  smartPadding: string;
}

const BOTTOM_NAV_HEIGHT = 64;

/**
 * Hook intelligent pour gérer l'espace disponible sur mobile
 * Calcule automatiquement le padding nécessaire pour éviter que le contenu soit masqué
 */
export function useViewportHeight(hideBottomNav: boolean = false): ViewportInfo {
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>({
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    availableHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    bottomInset: 0,
    hasBottomNav: !hideBottomNav,
    smartPadding: '0px',
  });

  useEffect(() => {
    const calculateViewport = () => {
      const height = window.innerHeight;
      
      // Détection plus robuste du safe area bottom inset
      const computedStyle = getComputedStyle(document.documentElement);
      let safeAreaBottom = computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || 
                          computedStyle.getPropertyValue('--safe-area-inset-bottom') ||
                          '0px';
      
      // Fallback pour iOS Safari qui peut ne pas exposer la propriété CSS correctement
      const bottomInset = parseFloat(safeAreaBottom) || 0;
      
      // Calcul de l'espace disponible
      const navHeight = hideBottomNav ? 0 : BOTTOM_NAV_HEIGHT;
      let availableHeight = height;
      if (!hideBottomNav) {
        availableHeight = height - navHeight;
      } else {
        availableHeight = height - bottomInset;
      }
      
      // Padding intelligent avec sécurité supplémentaire
      let smartPadding = '0px';
      if (!hideBottomNav) {
        // Nav visible: compenser nav + safe area pour que le contenu atteigne le vrai bas
        const calculatedPadding = BOTTOM_NAV_HEIGHT + bottomInset;
        smartPadding = `${calculatedPadding}px`;
      } else {
        // Nav cachée: uniquement le safe area
        smartPadding = safeAreaBottom || '0px';
      }

      setViewportInfo({
        height,
        availableHeight,
        bottomInset,
        hasBottomNav: !hideBottomNav,
        smartPadding,
      });
    };

    // Calcul initial
    calculateViewport();

    // Mise à jour sur resize/orientation change
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateViewport, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(timeoutId);
    };
  }, [hideBottomNav]);

  return viewportInfo;
}